import { Badge, Button, Card, Hero, Section } from '../components/ui';
import { Check, TrendingDown } from 'lucide-react';

const pricingTiers = [
  {
    range: '0 - 1,000',
    price: '$0.50',
    description: 'Perfect for getting started',
    savings: null
  },
  {
    range: '1,001 - 10,000',
    price: '$0.40',
    description: 'For growing teams',
    savings: '20% discount'
  },
  {
    range: '10,001 - 50,000',
    price: '$0.30',
    description: 'Mid-volume processing',
    savings: '40% discount'
  },
  {
    range: '50,001+',
    price: '$0.20',
    description: 'Enterprise volume',
    savings: '60% discount',
    highlight: true
  }
];

const pricingHighlights = [
  {
    title: 'Billing',
    description: 'Usage billed monthly with automatic volume discounts and transparent invoices.'
  },
  {
    title: 'Implementation',
    description: 'No setup fees, no commitments, and onboarding support included with every plan.'
  },
  {
    title: 'Security',
    description: 'SOC 2 controls, encryption, and audit logging available for every customer tier.'
  }
];

const includedFeatures = [
  'LLM-powered extraction',
  'Master data validation',
  'Human-in-the-loop review',
  'ERP integration exports',
  'Multi-tenant architecture',
  'API access & webhooks',
  'Role-based access control',
  'Audit logging',
  'Email support',
  'No setup fees'
];

const enterpriseFeatures = [
  {
    title: 'Volume Discounts',
    description: 'Automatic discounts as you scale. The more you process, the less you pay per document.'
  },
  {
    title: 'Dedicated Support',
    description: 'Priority support with SLA guarantees for volumes over 10,000 documents/month.'
  },
  {
    title: 'Custom Solutions',
    description: 'Custom ML models, on-premise deployment, SSO, and data residency options available.'
  }
];

const faqs = [
  {
    question: 'How does pay-as-you-go pricing work?',
    answer: 'You only pay for the documents you process each month. No subscriptions, no commitments. Pricing automatically adjusts based on your monthly volume.'
  },
  {
    question: 'What counts as a document?',
    answer: 'Each uploaded file counts as one document, regardless of page count. Multi-page PDFs are processed as a single document.'
  },
  {
    question: 'When do volume discounts apply?',
    answer: 'Volume discounts are calculated automatically at the end of each billing cycle based on your total monthly usage. You get the best rate for your total volume.'
  },
  {
    question: 'Is there a minimum commitment?',
    answer: 'No. There are no minimum volumes or commitments. Process as few or as many documents as you need each month.'
  },
  {
    question: 'Is there a free trial?',
    answer: 'Yes! Process your first 100 documents free. No credit card required to start.'
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit cards, ACH transfers, and wire transfers for enterprise customers.'
  },
  {
    question: 'How do enterprise discounts work?',
    answer: 'For volumes over 50,000 documents/month, contact our sales team for custom enterprise pricing with additional discounts and dedicated support.'
  }
];

export const Pricing = () => {
  return (
    <>
      <Hero
        title="Simple Pay-As-You-Go Pricing"
        description="Only pay for what you use. Automatic volume discounts as you scale. No subscriptions, no commitments."
        size="sm"
      />

      <Section centered>
        <div className="pricing-intro">
          <span className="pricing-intro__badge">
            <Check size={18} strokeWidth={1.75} aria-hidden />
            <span>First 100 documents free • No credit card required</span>
          </span>
          <h2>Volume-based pricing</h2>
          <p>Pay per document processed. Lower rates automatically apply as your volume grows.</p>
          <div className="pricing-intro__meta">
            {pricingHighlights.map((item) => (
              <div key={item.title}>
                <span>{item.title}</span>
                {item.description}
              </div>
            ))}
          </div>
        </div>

        <div className="pricing-grid">
          {pricingTiers.map((tier) => (
            <Card
              key={tier.range}
              elevation={tier.highlight ? 'xl' : 'lg'}
              bordered={tier.highlight}
              className={`pricing-card${tier.highlight ? ' pricing-card--highlight' : ''}`}
            >
              {tier.highlight && (
                <div className="pricing-card__badge">
                  <Badge variant="success" appearance="solid">
                    <span className="pricing-card__badge-icon" aria-hidden>
                      <TrendingDown size={14} strokeWidth={1.75} />
                    </span>
                    Best Value
                  </Badge>
                </div>
              )}

                <div className="pricing-card__content">
                  <div className="pricing-card__header">
                    <span className="pricing-card__range">{tier.range} docs/month</span>
                    <div className="pricing-card__price">
                      <span className="pricing-card__amount">{tier.price}</span>
                      <span className="pricing-card__period">/document</span>
                    </div>
                  {tier.savings && (
                    <div className="pricing-card__savings">
                      <Badge variant="success" appearance="soft">
                        {tier.savings}
                      </Badge>
                    </div>
                  )}
                  <p className="pricing-card__description">{tier.description}</p>
                </div>
                <div className="pricing-card__cta">
                  <Button
                    variant={tier.highlight ? 'primary' : 'outline'}
                    size="lg"
                    fullWidth
                    as="a"
                    href="/contact"
                  >
                    Talk to sales
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Everything Included" subtitle="All features available at every volume" background="subtle" centered>
        <div className="pricing-included">
          <Card elevation="lg" size="lg">
            <div className="pricing-included-grid">
              {includedFeatures.map((feature) => (
                <div key={feature} className="pricing-included-grid__item">
                  <Check size={18} strokeWidth={1.75} aria-hidden />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </Section>

      <Section title="Enterprise Solutions" subtitle="Custom solutions for high-volume processing" centered>
        <div className="features-advanced-grid">
          {enterpriseFeatures.map((feature) => (
            <Card key={feature.title} elevation="md" className="features-advanced-card">
              <div className="features-advanced-card__body">
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </div>
            </Card>
          ))}
        </div>
        <div style={{ textAlign: 'center', marginTop: 'var(--space-2xl)' }}>
          <Button variant="primary" size="lg" as="a" href="/contact">
            Contact Sales for Enterprise Pricing
          </Button>
        </div>
      </Section>

      <Section title="Frequently Asked Questions" subtitle="Everything you need to know about pricing" background="subtle" centered>
        <div className="pricing-faq">
          {faqs.map((faq) => (
            <Card key={faq.question} elevation="sm" className="pricing-faq__item">
              <h4>{faq.question}</h4>
              <p>{faq.answer}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section centered>
        <div className="pricing-cta">
          <h2>Ready to get started?</h2>
          <p>Process your first 100 documents free. No credit card required.</p>
          <div>
            <Button variant="primary" size="lg" as="a" href="/contact">
              Start Processing
            </Button>
            <Button variant="outline" size="lg" as="a" href="/contact">
              Contact Sales
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
};
